// @ts-nocheck
import * as signalR from '@microsoft/signalr';
import { Store } from 'redux';
import { RootAction } from 'typesafe-actions';

import Logger from 'common/services/Logger/Logger';

import {
  getSignalrEventType,
  SIGNALR_CONNECTED_TYPE,
  SIGNALR_INVOKE_TYPE,
  SIGNALR_START_TYPE,
  SIGNALR_STOP_TYPE,
  SIGNALR_TYPE,
} from './signalR.types';

const INIT_RECONNECT_TIMEOUT = 500;
const SERVER_TIMEOUT_MS = 100000;

const log = new Logger('SOCKET');

class SignalrConnection {
  activesubscribtions: { type: RootAction; payload: unknown }[];
  hub: string;
  connection: signalR.HubConnection;
  onCloseTimeout: number;
  store: Store;
  constructor({
    hub,
    hubEvents = [],
    store,
    url,
    onReconnected = null,
    onReconnecting = null,
    serverTimeoutInMilliseconds = SERVER_TIMEOUT_MS,
    activesubscribtions = [],
  }) {
    this.hub = hub;
    this.hubEvents = hubEvents;
    this.reconnectCounter = 0;
    this.reconnectTimeout = INIT_RECONNECT_TIMEOUT;
    this.store = store;
    this.subscribeIdsMap = {};
    this.url = url;
    this.onCloseTimeout = undefined;
    this.subscribtions = {};
    this.activesubscribtions = activesubscribtions;
    this.reconnecting = false;
    this.onReconnecting = onReconnecting;
    this.onReconnected = onReconnected;
    this.serverTimeoutInMilliseconds = serverTimeoutInMilliseconds;
    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(`${this.url}/${hub}`, {
        transport: signalR.HttpTransportType.WebSockets,
        skipNegotiation: true,
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          log.log('Retry', JSON.stringify(retryContext));
          return 3000;
        },
      })
      .configureLogging(signalR.LogLevel.Error)
      .build();
    this.connection.onreconnecting(() => {
      log.info('Reconnecting', this.hub);
      if (this.onReconnecting) this.onReconnecting();
    });
    this.connection.onreconnected(() => {
      log.success('Reconnected', this.hub);
      this.resubscribe();
      if (this.onReconnected) this.onReconnected();
    });
    this.connection.onclose(() => {
      log.error('Disconnected', this.hub);
      this.onCloseTimeout = setTimeout(this.start, 3000);
    });
    this.applyCallbackToHubMethods();
  }

  resubscribe = () => {
    if (!this.activesubscribtions?.length) return null;
    log.success('Resubscribe', this.hub);

    const activeSubscriptions = [...this.activesubscribtions];

    this.activesubscribtions = [];

    activeSubscriptions.forEach(this.store.dispatch);
  };

  applyCallbackToHubMethods = () => {
    this.hubEvents.forEach((event) => {
      if (event instanceof Object && typeof event.name === 'string' && typeof event.callback === 'function') {
        this.connection.on(event.name, event.callback);
      } else {
        this.connection.on(event, (msg) => {
          if (this.onReconnected) this.onReconnected();
          this.store.dispatch({
            type: getSignalrEventType(event, msg),
            payload: msg.data || msg,
          });
        });
      }
    });
  };

  invoke(methodName, rest) {
    let requestData = Array.isArray(rest) ? rest : [rest];
    if (!rest) {
      requestData = [];
    }

    return this.connection
      .invoke(methodName, ...requestData)
      .then((data) => {
        this.store.dispatch({
          type: getSignalrEventType(`${this.hub}/${methodName}`),
          payload: data,
        });
      })
      .catch((error) => {
        console.error(methodName, error);
      });
  }

  start = () =>
    this.connection
      .start()
      .then(() => {
        log.success('Started', this.hub);
        this.resubscribe();
        clearTimeout(this.onCloseTimeout);

        this.store.dispatch({
          type: SIGNALR_CONNECTED_TYPE,
        });
      })
      // .then(resolve)
      .catch((err) => {
        setTimeout(() => {
          this.start();
        }, 5000);
        log.error('Failed to start', this.hub, err);
      });

  stop() {
    return new Promise((resolve) => {
      this.connection
        .stop()
        .then(resolve)
        .catch(() => {
          this.stop();
        });
    });
  }

  storeSubscriptions(action, connection) {
    const { payload } = action;
    const { ref } = payload;

    const isSubscriptionGuidExist = ref && connection.activesubscribtions.find((i) => i.payload.id === ref);

    if (isSubscriptionGuidExist)
      connection.activesubscribtions = connection.activesubscribtions.filter((i) => i.payload.id !== ref);
    else connection.activesubscribtions.push(action);
  }
}

export const signalrMiddleware = (store) => (next) => {
  const connections = {};
  return (action) => {
    if (
      typeof action.type === 'function' ||
      action.type.indexOf(SIGNALR_TYPE) === -1 ||
      action.type.indexOf('SIGNALR_INCOME') !== -1
    ) {
      return next(action);
    }
    if (typeof action.payload.hub !== 'string') {
      throw Error('"hub" string is required in "action"');
    }

    if (action.type.indexOf(SIGNALR_START_TYPE) !== -1 && !Array.isArray(action.payload.hubEvents)) {
      throw Error('"hubEvents" should be an array of strings/objects in "action"');
    }
    if (action.type.indexOf(SIGNALR_INVOKE_TYPE) !== -1 && typeof action.payload.invokeType !== 'string') {
      throw Error('"invokeType" string is required in "action"');
    }

    const { hub, hubEvents, invokeType, subscribeId, data, url, onReconnected, onReconnecting } = action.payload;

    const startConnection = () => {
      connections[subscribeId] = new SignalrConnection({
        hub,
        hubEvents,
        store,
        url,
        onReconnecting,
        onReconnected,
      });
      return connections[subscribeId].start();
    };

    if (!window._wsSend) {
      window._wsSend = (type, payload) => {
        store.dispatch({ type, payload });
      };
    }

    const invokeAndHandleSubscribeId = () => {
      try {
        connections[subscribeId].storeSubscriptions(action, connections[subscribeId]);
        return connections[subscribeId].invoke(invokeType, data);
      } catch (error) {
        console.log('error => ', error);
      }
    };
    switch (action.type) {
      case SIGNALR_START_TYPE:
        if (connections[subscribeId]) {
          // TODO: Fix this the next time the file is edited.

          return new Promise((resolve) => resolve());
        }
        return startConnection().then(() => {
          if (invokeType && subscribeId) {
            return invokeAndHandleSubscribeId();
          }
          return next(action);
        });
      case SIGNALR_INVOKE_TYPE:
        if (window.__TEST__) return;
        if (action.payload.hubEvents) {
          const events = action.payload.hubEvents.filter((x) => !connections[subscribeId].hubEvents.includes(x));
          if (events.length) {
            connections[subscribeId].hubEvents.push(...events);
            connections[subscribeId].applyCallbackToHubMethods();
          }
        }
        invokeAndHandleSubscribeId();
        break;
      case SIGNALR_STOP_TYPE:
        if (!connections[subscribeId]) {
          break;
        }

        if (invokeType) {
          invokeAndHandleSubscribeId().then(() => {
            connections[subscribeId].stop();
            connections[subscribeId] = null;
          });
        } else {
          connections[subscribeId].stop();
          connections[subscribeId] = null;
        }
        break;
      default:
        break;
    }

    return next(action);
  };
};
